import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import axiosConn from '../../axiosConn';
import { MAX_IMAGE_SIZE } from '../../config/constants';
import { createMediaUploader } from '../../utils/mediaUpload';
import { getHitLabApiMessage, isHttpsUrl } from './hitLabUtils';

const CATALOG_DEFAULTS = {
  trackId: '',
  kind: 'track',
  status: 'draft',
  title: '',
  artistName: '',
  publicLabel: '',
  audioUrl: '',
  coverArtUrl: '',
  durationSeconds: '',
  displayOrder: '',
  admissionType: 'manual_admin',
  sourceType: 'manual_admin',
  rightsStatus: 'pending',
  radioPermissionConfirmed: false,
  adminNotes: '',
};

const STATUS_OPTIONS = [
  ['draft', 'Draft'],
  ['active', 'Active'],
  ['archived', 'Archived'],
];

const KIND_OPTIONS = [
  ['track', 'Track'],
  ['bumper', 'Bumper'],
];

const ADMISSION_OPTIONS = [
  ['manual_admin', 'Manual admin'],
  ['joe_galaxy_owned', 'Joe Galaxy owned'],
  ['curator_free', 'Curator free'],
  ['hitlab_paid', 'HitLab paid'],
  ['promo', 'Promo'],
];

const RIGHTS_OPTIONS = [
  ['pending', 'Pending'],
  ['owner_controlled', 'Owner controlled'],
  ['artist_confirmed', 'Artist confirmed'],
  ['admin_verified', 'Admin verified'],
];

const getCatalogItems = (response) => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

const withParams = (authConfig, params) => ({
  ...(authConfig || {}),
  params,
});

const getFieldValue = (item = {}, field) =>
  item[field] === null || item[field] === undefined ? '' : item[field];

const slugify = (value, fallback = 'radio-item') => {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
  return slug || fallback;
};

const getGeneratedTrackId = (form = {}) => {
  if (form.trackId) return form.trackId;
  if (form.kind === 'bumper') {
    return slugify(`bumper ${form.publicLabel || form.title}`, 'bumper');
  }
  return slugify(`${form.artistName} ${form.title}`, 'radio-track');
};

const hasGeneratedTrackIdSource = (form = {}) => {
  if (form.trackId) return true;
  if (form.kind === 'bumper') {
    return Boolean((form.publicLabel || form.title || '').trim());
  }
  return Boolean((form.artistName || '').trim() && (form.title || '').trim());
};

const getFormFromItem = (item = {}) => ({
  ...CATALOG_DEFAULTS,
  trackId: getFieldValue(item, 'trackId'),
  kind: getFieldValue(item, 'kind') || 'track',
  status: getFieldValue(item, 'status') || 'draft',
  title: getFieldValue(item, 'title'),
  artistName: getFieldValue(item, 'artistName'),
  publicLabel: getFieldValue(item, 'publicLabel'),
  audioUrl: getFieldValue(item, 'audioUrl'),
  coverArtUrl: getFieldValue(item, 'coverArtUrl'),
  durationSeconds: getFieldValue(item, 'durationSeconds'),
  displayOrder: getFieldValue(item, 'displayOrder'),
  admissionType: getFieldValue(item, 'admissionType') || 'manual_admin',
  sourceType: getFieldValue(item, 'sourceType') || 'manual_admin',
  rightsStatus: getFieldValue(item, 'rightsStatus') || 'pending',
  radioPermissionConfirmed: Boolean(item.radioPermissionConfirmed),
  adminNotes: getFieldValue(item, 'adminNotes'),
});

export default function HitLabRadioCatalogManager({ authConfig }) {
  const uploaderRef = useRef(null);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ status: '', kind: '' });
  const [form, setForm] = useState(CATALOG_DEFAULTS);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const uploader = useMemo(() => {
    if (!uploaderRef.current) {
      uploaderRef.current = createMediaUploader({
        axiosInstance: axios,
        maxImageBytes: MAX_IMAGE_SIZE,
        videoMimeTypes: new Set(),
        isVideoName: () => false,
        forceAutoResourceType: true,
        enableVideoMetaCheck: false,
        enableThumbnails: false,
      });
    }
    return uploaderRef.current;
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!authConfig) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.kind) params.kind = filters.kind;
      const response = await axiosConn.get(
        'hitlab/admin/radio/catalog',
        withParams(authConfig, params),
      );
      setItems(getCatalogItems(response));
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Radio catalog could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [authConfig, filters.kind, filters.status]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(
    () => () => {
      if (coverPreviewUrl) URL.revokeObjectURL?.(coverPreviewUrl);
    },
    [coverPreviewUrl],
  );

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const generatedTrackId = getGeneratedTrackId(form);
  const showGeneratedTrackId = hasGeneratedTrackIdSource(form);
  const generatedTrackIdPlaceholder =
    form.kind === 'bumper'
      ? 'Generated after label or title'
      : 'Generated after artist and title';

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const clearCoverFile = () => {
    if (coverPreviewUrl) URL.revokeObjectURL?.(coverPreviewUrl);
    setCoverFile(null);
    setCoverPreviewUrl('');
    setUploadProgress(0);
  };

  const setCoverImageFile = (file) => {
    setError('');
    if (!file) return;
    try {
      uploader.validate(file);
      clearCoverFile();
      setCoverFile(file);
      setCoverPreviewUrl(URL.createObjectURL?.(file) || '');
    } catch (err) {
      setError(err?.message || 'Cover art image could not be used.');
    }
  };

  const handleCoverPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find((entry) =>
      String(entry.type || '').startsWith('image/'),
    );
    if (file) setCoverImageFile(file);
  };

  const handleCoverDrop = (event) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer?.files || []).find((entry) =>
      String(entry.type || '').startsWith('image/'),
    );
    if (file) setCoverImageFile(file);
  };

  const resetForm = () => {
    setForm(CATALOG_DEFAULTS);
    clearCoverFile();
  };

  const validateForm = () => {
    if (!form.title.trim()) return 'Title is required.';
    if (!isHttpsUrl(form.audioUrl)) return 'Audio URL must start with https://.';
    if (form.coverArtUrl && !isHttpsUrl(form.coverArtUrl)) {
      return 'Cover art URL must start with https://.';
    }
    if (form.status === 'active') {
      if (form.rightsStatus === 'pending') {
        return 'Active catalog items require approved rights.';
      }
      if (!form.radioPermissionConfirmed) {
        return 'Active catalog items require Radio permission.';
      }
      if (form.kind === 'track') {
        if (!form.artistName.trim()) return 'Active tracks require artist name.';
        if (!coverFile && !form.coverArtUrl.trim()) {
          return 'Active tracks require cover art.';
        }
      }
    }
    return '';
  };

  const submitCatalogItem = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!authConfig) {
      setError('Please sign in again to continue your artist intake.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setUploadProgress(0);
    try {
      let coverArtUrl = form.coverArtUrl.trim();
      if (coverFile) {
        const upload = await uploader.upload(coverFile, {
          publicIdPrefix: 'HitLabRadio/CoverArt',
          onProgress: setUploadProgress,
        });
        coverArtUrl = upload.transformedUrl || upload.secureUrl || coverArtUrl;
      }

      const payload = {
        kind: form.kind,
        status: form.status,
        title: form.title.trim(),
        artistName: form.kind === 'track' ? form.artistName.trim() : '',
        publicLabel:
          form.kind === 'bumper'
            ? (form.publicLabel || form.title).trim()
            : '',
        audioUrl: form.audioUrl.trim(),
        coverArtUrl,
        durationSeconds: Number(form.durationSeconds) || 0,
        admissionType: form.admissionType,
        sourceType: 'manual_admin',
        rightsStatus: form.rightsStatus,
        radioPermissionConfirmed: Boolean(form.radioPermissionConfirmed),
        supportEligible: false,
        adminNotes: form.adminNotes,
      };
      if (String(form.displayOrder).trim()) {
        payload.displayOrder = Number(form.displayOrder) || 0;
      }
      if (String(form.trackId).trim()) {
        payload.trackId = form.trackId.trim();
      }

      const response = await axiosConn.post(
        'hitlab/admin/radio/catalog/upsert',
        payload,
        authConfig,
      );
      const savedTrackId =
        response?.data?.data?.item?.trackId || form.trackId || generatedTrackId;
      setMessage('Radio catalog item saved.');
      clearCoverFile();
      setForm((current) => ({
        ...current,
        trackId: savedTrackId,
        coverArtUrl,
      }));
      await loadCatalog();
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Radio catalog item could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const editItem = (item) => {
    setError('');
    setMessage('');
    clearCoverFile();
    setForm(getFormFromItem(item));
  };

  return (
    <section className="hitlab-radio-catalog-admin" aria-label="Joe Galaxy Radio Catalog">
      <div className="hitlab-radio-catalog-admin-header">
        <div>
          <p className="hitlab-kicker">RADIO CATALOG</p>
          <h2>Joe Galaxy Radio Catalog</h2>
          <p>
            Add approved songs and bumper MP3s to Joe Galaxy Radio using hosted
            HTTPS audio URLs and optional cover art.
          </p>
        </div>
        <button
          type="button"
          className="hitlab-secondary-btn"
          onClick={loadCatalog}
          disabled={loading}
        >
          Refresh catalog
        </button>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? (
        <div className="hitlab-alert hitlab-alert-success">{message}</div>
      ) : null}

      <div className="hitlab-radio-catalog-grid">
        <form className="hitlab-radio-catalog-form" onSubmit={submitCatalogItem}>
          <div className="hitlab-radio-catalog-form-top">
            <h3>Catalog item</h3>
            <button
              type="button"
              className="hitlab-table-action"
              onClick={resetForm}
            >
              New item
            </button>
          </div>

          <div className="hitlab-listen-form-grid">
            <label>
              Generated track ID
              <input
                value={showGeneratedTrackId ? generatedTrackId : ''}
                placeholder={generatedTrackIdPlaceholder}
                maxLength="120"
                readOnly
              />
            </label>
            <label>
              Kind
              <select
                value={form.kind}
                onChange={(event) => updateForm('kind', event.target.value)}
              >
                {KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(event) => updateForm('status', event.target.value)}
              >
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Title
              <input
                value={form.title}
                maxLength="160"
                onChange={(event) => updateForm('title', event.target.value)}
                required
              />
            </label>
            <label>
              Artist name
              <input
                value={form.artistName}
                maxLength="120"
                onChange={(event) => updateForm('artistName', event.target.value)}
                disabled={form.kind === 'bumper'}
              />
            </label>
            {form.kind === 'bumper' ? (
              <label>
                Bumper display label
                <input
                  value={form.publicLabel}
                  maxLength="160"
                  onChange={(event) =>
                    updateForm('publicLabel', event.target.value)
                  }
                />
              </label>
            ) : null}
            <label className="hitlab-form-wide">
              Audio URL
              <input
                type="url"
                value={form.audioUrl}
                onChange={(event) => updateForm('audioUrl', event.target.value)}
                required
              />
            </label>
            <label className="hitlab-form-wide">
              Cover art URL
              <input
                type="url"
                value={form.coverArtUrl}
                onChange={(event) => updateForm('coverArtUrl', event.target.value)}
              />
            </label>
            <div className="hitlab-radio-catalog-readonly">
              {form.durationSeconds
                ? `${form.durationSeconds} seconds detected`
                : 'Duration auto-detects from audio.'}
            </div>
            <div className="hitlab-radio-catalog-readonly">
              {form.displayOrder
                ? `Current order ${form.displayOrder}`
                : 'New items append to the end.'}
            </div>
            <label>
              Admission type
              <select
                value={form.admissionType}
                onChange={(event) =>
                  updateForm('admissionType', event.target.value)
                }
              >
                {ADMISSION_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Rights status
              <select
                value={form.rightsStatus}
                onChange={(event) => updateForm('rightsStatus', event.target.value)}
              >
                {RIGHTS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="hitlab-radio-catalog-check">
              <input
                type="checkbox"
                checked={form.radioPermissionConfirmed}
                onChange={(event) =>
                  updateForm('radioPermissionConfirmed', event.target.checked)
                }
              />
              <span>Radio permission confirmed</span>
            </label>
            <div className="hitlab-radio-catalog-readonly">
              Support disabled in V1.
            </div>
            <label className="hitlab-form-wide">
              Admin notes
              <textarea
                value={form.adminNotes}
                maxLength="2000"
                onChange={(event) => updateForm('adminNotes', event.target.value)}
              />
            </label>
          </div>

          <div
            className="hitlab-radio-cover-drop"
            onPaste={handleCoverPaste}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleCoverDrop}
          >
            <label>
              Cover image upload
              <input
                type="file"
                accept="image/*"
                onChange={(event) => setCoverImageFile(event.target.files?.[0])}
              />
            </label>
            {coverPreviewUrl || form.coverArtUrl ? (
              <img
                src={coverPreviewUrl || form.coverArtUrl}
                alt="Radio catalog cover preview"
                width="120"
                height="120"
              />
            ) : null}
            {coverFile ? (
              <button
                type="button"
                className="hitlab-table-action"
                onClick={clearCoverFile}
              >
                Clear cover file
              </button>
            ) : null}
            {uploadProgress > 0 && uploadProgress < 100 ? (
              <span>{uploadProgress}% uploaded</span>
            ) : null}
          </div>

          {form.audioUrl ? (
            <audio
              className="hitlab-radio-catalog-audio"
              src={form.audioUrl}
              controls
              preload="metadata"
              onLoadedMetadata={(event) => {
                const nextDuration = Math.round(
                  Number(event.currentTarget.duration) || 0,
                );
                if (nextDuration > 0) {
                  updateForm('durationSeconds', String(nextDuration));
                }
              }}
            />
          ) : null}

          <div className="hitlab-listen-actions">
            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={saving}
            >
              {saving ? 'Saving' : 'Save catalog item'}
            </button>
          </div>
        </form>

        <div className="hitlab-radio-catalog-list">
          <div className="hitlab-radio-catalog-filters">
            <label>
              Status
              <select
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Kind
              <select
                value={filters.kind}
                onChange={(event) => updateFilter('kind', event.target.value)}
              >
                <option value="">All kinds</option>
                {KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? <div className="hitlab-alert">Loading Radio catalog...</div> : null}
          <div className="hitlab-radio-catalog-items">
            {items.map((item) => (
              <article key={item.trackId} className="hitlab-radio-catalog-item">
                <div>
                  <span>{item.kind}</span>
                  <strong>{item.title}</strong>
                  <small>{item.trackId}</small>
                  <small>{item.status}</small>
                </div>
                <button
                  type="button"
                  className="hitlab-table-action"
                  onClick={() => editItem(item)}
                >
                  Edit
                </button>
              </article>
            ))}
            {!items.length && !loading ? (
              <div className="hitlab-alert">No Radio catalog items yet.</div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
